/**
 * Third-person spring-arm camera, deliberately kept independent of input,
 * physics, and scene ownership. The integrator supplies a target and, once
 * physics exists, a sphere-sweep; that keeps this rig usable from render code
 * without coupling it to a physics backend or to a particular character controller.
 */

import * as THREE from 'three';
import { moveBasisFromYaw, type MoveBasis } from '@/camera/moveBasis';

export type CameraMode = 'follow' | 'crouch' | 'roll' | 'pen';

/**
 * The aspect every framing in this file was authored and signed off at (G2).
 * At or above it the rig is exactly what it was; the two adaptations below only
 * act on a frame NARROWER than this.
 */
export const AUTHORED_ASPECT = 16 / 9;

/** How strongly the lens widens as the frame narrows: `tan(fov/2)` grows as `(16:9 / aspect)^p`. */
const LENS_WIDEN_POWER = 0.35;
/** The most the lens may widen, as a factor on `tan(fov/2)`: follow's 50° becomes 68°, not a fisheye. */
const LENS_WIDEN_MAX = 1.45;

/**
 * Factor on `tan(vfov / 2)` for a frame of this aspect.
 *
 * The FOV here is VERTICAL, so a portrait phone kept the 16:9 frame's height
 * and lost most of its width: follow's 50° was 79° wide at 16:9 and 24° wide
 * at 390 × 844, about 0.8 m of world at her distance. She walked out of that
 * sideways faster than the camera turns.
 *
 * Scaling `tan(fov/2)` — not the angle, and not a floor shared by every mode —
 * keeps every lens's width RELATIVE to the others: crouch stays tighter than
 * follow, roll wider, and focus magnifies by exactly what it did before. A
 * common floor would have collapsed all four to one value on a phone and taken
 * WATCH's magnification with it. 1 at and above `AUTHORED_ASPECT`, continuous,
 * capped: at 0.46 follow is 68° tall and 35° wide.
 */
export function aspectLensScale(aspect: number): number {
  if (!Number.isFinite(aspect) || aspect <= 0 || aspect >= AUTHORED_ASPECT) return 1;
  return Math.min(LENS_WIDEN_MAX, (AUTHORED_ASPECT / aspect) ** LENS_WIDEN_POWER);
}

/** A vertical FOV in degrees, widened for this aspect's lens scale. */
export function adaptFovForAspect(fovDegrees: number, aspect: number): number {
  const scale = aspectLensScale(aspect);
  if (scale === 1) return fovDegrees;
  const half = THREE.MathUtils.degToRad(fovDegrees) / 2;
  return THREE.MathUtils.radToDeg(2 * Math.atan(Math.tan(half) * scale));
}

/**
 * On a frame narrower than `AUTHORED_ASPECT`, how far off-centre (in NDC of the
 * frame's narrower half-extent) the rig's rotation lag may carry her aim point.
 *
 * The rig aims AT her, and slerps toward that aim at `rotationDamping`; the lag
 * is what a sideways walk shows. At 16:9 it is a readable drift. In a 24°-wide
 * portrait frame the same angle is the whole half-frame, and she left it
 * entirely within 1.2 s of holding the stick right. Capping the lag in NDC
 * rather than in degrees makes it the same fraction of the frame on every
 * screen: she drifts to 0.5 and the view carries her from there.
 */
const NARROW_FRAME_MAX_LAG_NDC = 0.5;

/** One tunable per framing decision. */
export interface SpringArmTuning {
  /**
   * Per mode: arm length, height above the target's ground point, look-at height,
   * FOV, shoulder offset, and how far the arm may climb when it cannot fit.
   */
  modes: Record<CameraMode, {
    distance: number; height: number; lookHeight: number; fov: number; shoulder: number;
    /**
     * Extra pitch, in radians, the arm may add when the collision sweep says
     * there is no room behind her. Zero everywhere except in a pen.
     *
     * See `#rise`. This is per mode rather than global because `follow` passed
     * G2 with a human at the keyboard and a camera that starts climbing walls is
     * not the camera he signed off.
     */
    rise: number;
    /**
     * Per-mode floor on the arm length, overriding `minDistance`.
     *
     * `minDistance` is a global "we would rather clip a wall than put the lens
     * inside her" trade, and it is the right trade in a room. **In a pen it is
     * the bug**: a creep is 0.54 m deep, so every sweep hit is closer than
     * 0.55 m, the clamp overrides all of them, and the lens is placed inside the
     * partition every single frame. That is what `2b-gameplay.png` was a picture
     * of, twice, for two different reasons.
     */
    minDistance: number;
    /**
     * How much of the lens's height above her look point the arm gives back as
     * the room shortens it, 0..1. At 1 the lens slides down toward her in
     * proportion to the arm lost, so a short arm keeps the long arm's angle
     * onto her instead of folding into a plan view of her back.
     *
     * See `#settleLens`. Zero in `pen`, whose blocked arm climbs instead
     * (`rise`): in a creep the free space is above the partitions, not below.
     */
    settle: number;
  }>;
  positionDamping: number;
  rotationDamping: number;
  fovDamping: number;
  modeBlendTime: number;
  mouseSensitivity: number;
  pitchMin: number;
  pitchMax: number;
  collisionRadius: number;
  collisionRecoverSpeed: number;
  /**
   * Hard floor on the arm length, whatever the collision sweep says.
   *
   * Without it a sweep that hits close pulls the arm to zero and parks the lens
   * at the pivot — looking straight down at the top of her head, from inside
   * her. That is far worse than clipping a corner of the wall, and it is the
   * failure the first in-engine screenshots caught.
   *
   * **Now only the initial value**; each mode carries its own, and the trade it
   * describes is only correct in a room bigger than the arm. See the per-mode
   * `minDistance` above.
   */
  minDistance: number;
  autoYawFollow: number;
  autoYawDelay: number;
  /**
   * How fast the arm climbs, and settles back down, when it is blocked.
   *
   * Deliberately slower than `positionDamping`: the climb is a framing decision
   * the camera is making on the player's behalf, and one that snaps reads as the
   * room shoving the lens rather than as the camera finding somewhere to stand.
   */
  riseDamping: number;
  /** Absolute ceiling on pitch once `rise` is added. Guards against a top-down lens. */
  risePitchMax: number;
  /** Spec §7's `focus`: what the rig does while she is watching something. */
  focus: FocusTuning;
}

/**
 * The `focus` verb's effect on the rig (D-066).
 *
 * A modifier laid over whatever mode is live, not a mode of its own. She can
 * focus from the pen, from the walkway, mid-creep; making it a `CameraMode`
 * would have meant every mode needing a focused twin and `cameraModeFor` — a
 * pure function of gait and position — having to know about a held key.
 *
 * Every value here is applied in proportion to the eased weight, so releasing
 * `observe` is the same move played backwards rather than a cut.
 */
export interface FocusTuning {
  /** Pushed FOV at full weight. The magnification IS the verb. */
  fov: number;
  /**
   * Where she sits in the focused frame, as an NDC height: -1 is the bottom
   * edge, 0 the centre.
   *
   * **This replaced leading the look point toward the subject, which was the
   * obvious design and was wrong.** A lead is a fraction of an unbounded
   * distance, so at 17 m it swung the aim ten degrees off her and she left the
   * bottom of the frame completely — `1-pen-unfocused.png`'s opposite failure
   * and no better. What is wanted is not "aim between them", it is a
   * *composition*: put the pig on this line and let the thing she is watching
   * land where the geometry puts it, which for a man on the floor of a long hall
   * is the upper half of the frame.
   *
   * It is exact rather than approximate — `#buildLookAt` inverts the projection
   * for it — so she holds this line at any range, in any mode, at any FOV.
   */
  foregroundLine: number;
  /**
   * Floor on the arm, in metres, while she is watching. The sweep may not pull
   * it shorter than this; hits further out still stop it.
   *
   * **This is the trade that makes the feature work, and the first two attempts
   * at it were both wrong in an instructive way.** Inside a pen there is no room
   * behind her — that is the whole of D-065 — so the sweep collapses the arm onto
   * her and `rise` climbs to its ceiling looking for somewhere to stand; the lens
   * ends up a metre and a half up over the crate tops with the pig it is
   * following nowhere in the frame. Ignoring the sweep outright fixes that and
   * buys a worse bug: her bay is the NEAR one, the end wall is 1.2 m behind it,
   * and an unrestrained 1.3 m arm parks the lens outside the building.
   *
   * A floor keeps both honest. 40 mm of crate pipe between the lens and the shot
   * is a smaller sin than the shot not existing — the same trade `minDistance`
   * makes one field away — while 150 mm of end wall is still allowed to stop it.
   */
  armFloor: number;
  /** How fast the arm swings onto the bearing to the subject, per second. */
  yawRate: number;
  /** Weight ease-in and ease-out rates, per second. In is faster than out. */
  engageSpeed: number;
  releaseSpeed: number;
}

/**
 * M0a framing first guesses, awaiting the dedicated framing pass. These values
 * are intentionally tuned around a 0.32 m-tall character: conventional human
 * third-person defaults would reduce Rosalie to a dot or put the lens in the floor.
 */
export const ROSALIE_CAMERA: SpringArmTuning = {
  modes: {
    follow: {
      // 1.85 m. Pulled in from 2.2 after the first in-engine framing pass: at
      // 2.2 she was a small shape in a large empty floor, and her silhouette is
      // the thing the player has to read at all times.
      distance: 1.85,
      // 0.72 m. Lower than the obvious choice, because a high camera looking
      // down on a 0.32 m character flattens her into a blob and hides exactly
      // the roundness the whole design rests on (D-002).
      height: 0.72,
      // 0.24 m sits just under the top of her body, so she holds the centre of
      // frame with the floor ahead of her still legible.
      lookHeight: 0.24,
      // 50 degrees. Wider reads as a wide-angle speck at her scale; narrower
      // loses the "built for boots, not for her" architecture around her.
      fov: 50,
      // "0.22 m gives an over-shoulder read while staying modest beside Rosalie's 0.34 m width."
      shoulder: 0.22,
      rise: 0,
      minDistance: 0.55,
      // 1: the short arm keeps the long arm's angle exactly. 0.6 was shot too
      // and put the lens back over her: the trailer in beat 6's look from the
      // bins and Holly in beat 9's from the shut door both left the top of the
      // frame (`docs/verification/arm-drop/`, D-248).
      settle: 1,
    },
    crouch: {
      // "1.45 m brings crouch-low close enough to feel exposed rather than observational."
      distance: 1.45,
      // "0.36 m keeps the lens near Rosalie's body without asking a floor-level camera to survive every surface."
      height: 0.36,
      // "0.16 m meets snout height, making the crouch framing vulnerable and intimate."
      lookHeight: 0.16,
      // "43 degrees narrows the view so crouch-low feels confined instead of merely zoomed."
      fov: 43,
      // "0.12 m retains a shoulder cue but avoids hiding a 0.34 m-wide body behind the camera offset."
      shoulder: 0.12,
      rise: 0,
      minDistance: 0.55,
      settle: 0,
    },
    roll: {
      // 2.5 m. Still further back than follow — a roll needs runway in frame —
      // but 3.2 m made her a dot, which is the opposite of showcasing the verb
      // spec §5.4 calls the only joy in Act 1.
      distance: 2.5,
      // 0.45 m drops the chase camera below normal follow height while leaving
      // room above the floor.
      height: 0.45,
      // "0.18 m keeps the roll's low centre of mass as the visual anchor."
      lookHeight: 0.18,
      // 58 degrees. Wider than follow so speed reads at the frame edges, which
      // is where a sense of velocity actually comes from.
      fov: 58,
      // "0.08 m recentres the chase view so direction and momentum, not a shoulder, lead the composition."
      shoulder: 0.08,
      rise: 0,
      minDistance: 0.55,
      settle: 0,
    },
    /**
     * Inside a farrowing pen, where a third-person arm has nowhere to be.
     *
     * A creep is 0.54 m deep. That number is not negotiable — it is taken from a
     * real crate, and her mother cannot turn around because 1.72 m of sow does
     * not fit in 0.65 m of lane. The moment those dimensions become adjustable
     * for camera reasons the beat stops meaning anything (R-014).
     *
     * So the camera gives way instead. This is spec §4's `crouch-low` framing —
     * "vulnerable and intimate", which is the register of beat 1 — pulled in
     * further still, and given permission to CLIMB when the sweep says there is
     * no room behind her. The alternative that shipped was an arm clamped at
     * `minDistance` and driven straight into the partition, which is what
     * `docs/verification/m1/born/2b-gameplay.png` is a picture of: a grey wall.
     */
    pen: {
      // 1.10 m, up from 0.82.
      //
      // **The shorter arm was not short enough to be intimate, it was short
      // enough to be blind.** `docs/verification/m1/born/2b-gameplay.png` is
      // roughly 40 % of her own rump seen from above, with no mother, no
      // littermates, no lamp and no room in it — and the taking, measured, then
      // happened entirely outside the frustum (`docs/verification/m1/taking/`,
      // `removal.frustumContains: false`). The beat's whole event was off-camera.
      //
      // Shot before it was changed: nine candidate framings, two facings, in
      // `docs/verification/m1/pen-camera/`. Distance ALONE fixes nothing —
      // `back-b` is this arm at the old height and is still a wall of rump,
      // because the sweep crushes it back to the floor and `rise` then tips the
      // lens onto her back. Height is the lever; see below.
      distance: 1.10,
      // 0.62 m, up from 0.40, and this is the change that mattered.
      //
      // The pen is asymmetric and the old height was inside the short side of
      // it. Between her and her mother is a 1.09 m lane partition; BEHIND her is
      // a creep kerb only 0.35 m tall. A pivot at 0.40 m has the kerb in the way
      // of every backward sweep, so the arm collapses to `minDistance` whichever
      // way she faces. Lift it past 0.47 m — the kerb top plus the sweep radius
      // — and the arm can lie back over the kerb into open air above the pen
      // floor, which is where there was room the whole time.
      //
      // This is what Alex asked for ("a bit more distance but use the camera
      // more from the top to prevent wall collision") and it is worth being
      // precise about why it works: it is not that a higher lens sees over the
      // wall, it is that a higher PIVOT gives the sweep somewhere to go.
      height: 0.62,
      // 0.16 m, with the lens above her: aimed at her shoulders rather than her
      // snout, which keeps the straw in front of her — the thing the beat asks
      // her to root in, and the patch it later asks her to find empty — inside
      // the lower third of the frame instead of under it.
      lookHeight: 0.16,
      // 50 degrees, matching `follow`. Crouch's 43 was tried and a 2.4 m bay
      // through it reads as a keyhole; the confinement here is the room's job,
      // not the lens's.
      fov: 50,
      // Almost centred. A shoulder offset in a 0.54 m slot only ever buys a
      // faceful of partition.
      shoulder: 0.05,
      // 0.46 rad — about 26 degrees of climb. Enough to clear the lane
      // partition and look down into the creep; short of the top-down shot that
      // would turn the beat into a diorama, which 0.62 gave.
      //
      // Unchanged, deliberately, even though the arm and the pivot both moved.
      // The sweep shot two candidates that traded climb for height (`back-up-c`
      // at 1.15 m and `wide-up`) and both arrive at exactly the diorama this
      // number was lowered to avoid: in `08-sow-back-up-c.png` she is a small
      // teardrop seen from straight above, and a pig with no silhouette is a pig
      // the player cannot read (D-002).
      rise: 0.46,
      // 0.30 m. Close enough that she fills a third of the frame, which is what
      // "vulnerable and intimate" costs in a slot this size, and short enough
      // that the sweep is actually obeyed instead of being overridden.
      minDistance: 0.3,
      settle: 0,
    },
  },
  // "14 / s lets position settle quickly after movement while remaining frame-rate independent."
  positionDamping: 14,
  // "6 / s is deliberately lower than position damping, leaving the turn lag that gives the rig weight."
  rotationDamping: 6,
  // "9 / s lets FOV changes breathe with a mode shift without visibly trailing the framing."
  fovDamping: 9,
  // "0.28 s makes framing changes read as an intentional eased move, never a camera snap."
  modeBlendTime: 0.28,
  // "0.0025 radians per pixel gives mouse control enough precision for close, low framing."
  mouseSensitivity: 0.0025,
  // "-0.55 radians permits an upward look while retaining a useful horizon in the small-scale world."
  pitchMin: -0.55,
  // "0.55 radians permits looking down while the ground clamp protects this low camera from a floor lens."
  pitchMax: 0.55,
  // "0.12 m sweeps a camera-sized volume, not a point that can visibly cut through the test-room wall."
  collisionRadius: 0.12,
  // "3.5 m/s restores room behind Rosalie briskly but never with the wall-pop of an instant extension."
  collisionRecoverSpeed: 3.5,
  // "0.55 m still frames a 0.32 m character; anything shorter is inside her."
  minDistance: 0.55,
  // "2.4 / s gently recentres only an idle camera, so player look input always remains authoritative."
  autoYawFollow: 2.4,
  // "1.1 s leaves enough time to inspect the room before the camera starts helping with her facing."
  autoYawDelay: 1.1,
  // 4.5 / s. Slower than position damping so the climb reads as a decision.
  riseDamping: 4.5,
  // 0.98 rad. Past this the lens is looking at the top of her back, and D-002
  // says her silhouette is what the player reads — from directly above there
  // isn't one.
  risePitchMax: 0.98,
  focus: {
    // 32 degrees against `pen`'s and `follow`'s 50: a 1.6× push. Beat 2 asks the
    // player to read a man twenty metres away, and at 50 degrees he is about
    // forty pixels of trouser.
    fov: 32,
    // -0.78: her back low in the frame with a little deck under it, which leaves
    // the far trough at about four fifths of the way up. Measured, not guessed —
    // `focus.spec.ts` prints where the worker lands, and this line is the only
    // knob that moves BOTH of them, so it is the one that has to be right.
    foregroundLine: -0.78,
    // 0.95 m against `pen`'s authored 1.10 and its 0.30 floor. Long enough that
    // the lens is behind her rather than on her; 0.15 m short of the end wall
    // behind the near bay, which is the tightest place this ever has to work.
    armFloor: 0.95,
    // 4.0 / s. Faster than `autoYawFollow`'s 2.4, because this is the player
    // asking for the shot rather than the camera tidying up after them; slow
    // enough that the swing is a move and not a snap.
    yawRate: 4.0,
    // 6 / s in, 3.5 / s out. Holding `Q` should feel like the shot arriving;
    // letting go should feel like it settling back, and settling takes longer
    // than arriving in every camera worth copying.
    engageSpeed: 6,
    releaseSpeed: 3.5,
  },
};

/** Raycast against the world, supplied by the integrator so this module has no physics dependency. */
const DOWN = new THREE.Vector3(0, -1, 0);

export type SweepFn = (
  from: THREE.Vector3, dir: THREE.Vector3, maxDistance: number, radius: number,
) => number | null;

/**
 * A camera-only spring arm. It receives real render-frame time because camera
 * motion is presentation, not simulation: every smoothing coefficient therefore
 * uses `1 - exp(-k * dt)` instead of a frame-rate-dependent lerp constant.
 */
export class SpringArmCamera {
  readonly camera: THREE.PerspectiveCamera;

  #target = new THREE.Vector3();
  #pivot = new THREE.Vector3();
  #desiredPosition = new THREE.Vector3();
  #safePosition = new THREE.Vector3();
  #sweepDirection = new THREE.Vector3();
  #forward = new THREE.Vector3();
  #right = new THREE.Vector3();
  #lookAt = new THREE.Vector3();
  #up = new THREE.Vector3(0, 1, 0);
  #lookMatrix = new THREE.Matrix4();
  #targetQuaternion = new THREE.Quaternion();
  #rigPosition = new THREE.Vector3();
  #rigQuaternion = new THREE.Quaternion();

  #releasePosition = new THREE.Vector3();
  #releaseQuaternion = new THREE.Quaternion();
  #releaseFov = 0;

  #facing = 0;
  #yaw = 0;
  #pitch = 0;
  #mouseIdle = 0;
  #mouseMoved = false;
  #lookHeld = false;
  #aspect = AUTHORED_ASPECT;

  #mode: CameraMode = 'follow';
  #distance: number;
  #height: number;
  #lookHeight: number;
  #framingFov: number;
  #shoulder: number;
  #blendDistance = 0;
  #blendHeight = 0;
  #blendLookHeight = 0;
  #blendFov = 0;
  #blendShoulder = 0;
  #modeBlendElapsed = 0;
  #modeBlending = false;

  /**
   * How much extra pitch the current mode is ALLOWED to add, and how much it is
   * currently using.
   *
   * `#rise` is a damped feedback loop and not a search: each frame it steers
   * toward `blockage × allowance`, where blockage is how much of the arm the
   * sweep took away. As the lens climbs it clears the obstruction, blockage
   * falls, and the loop settles at the height where it *just* fits. That
   * self-terminates, which the obvious alternative — step the pitch up until a
   * sweep comes back clean — does not, and it costs one sweep a frame rather
   * than five.
   *
   * It is deliberately fed the PREVIOUS frame's blockage. Re-sweeping to
   * convergence inside one frame would be exact and would also be the camera
   * teleporting; the lag here is the smoothing.
   */
  #rise = 0;
  #riseAllowance: number;
  #blendRise = 0;
  /** The current mode's arm floor, blended like every other framing value. */
  #minDistance: number;
  #blendMinDistance = 0;
  /** The current mode's `settle`, blended like every other framing value. */
  #settle: number;
  #blendSettle = 0;

  /**
   * `focus`: where she is looking, whether she is still looking there, and how
   * far the rig has committed to it (D-066).
   *
   * `#focusPoint` is deliberately NOT cleared when focus ends. The weight eases
   * back to zero over a third of a second and every focused term is scaled by
   * it, so the release has to keep reading the same geometry it engaged on —
   * zeroing the point instead makes the lens sweep toward the world origin on
   * the way out, which is a whip pan to nowhere.
   */
  #focusPoint = new THREE.Vector3();
  #focusActive = false;
  #focusWeight = 0;
  /** This frame's FOV after `focus` is laid over the blended mode value. */
  #effectiveFov: number;
  /** This frame's arm floor: the mode's, or the focus floor while she watches. */
  #effectiveMinDistance: number;

  #armDistance: number;
  #rigFov: number;
  #overridden = false;
  /** The desk tally enters as a held look, not a hard cinematic cut. */
  #shotEaseIn = false;
  #releasing = false;
  #releaseElapsed = 0;
  #disposed = false;

  readonly tuning: SpringArmTuning;

  constructor(aspect: number, tuning: SpringArmTuning) {
    /*
     * **Cloned, not held by reference.**
     *
     * `ROSALIE_CAMERA` is a module-level const that everything imports, and the
     * rig used to keep the caller's object itself. `setCameraTuning` — the debug
     * seam the framing sweep drives — writes into `tuning.modes[mode]`, so nine
     * candidate framings in one page context permanently rewrote the shipped
     * table, with no restore path and no way to tell afterwards. The next thing
     * to read `ROSALIE_CAMERA.modes.pen` got whatever the sweep left behind.
     *
     * Found on independent review. One level deep is enough: `modes` is the only
     * nested object and its entries are flat numbers.
     */
    this.tuning = {
      ...tuning,
      modes: {
        follow: { ...tuning.modes.follow },
        crouch: { ...tuning.modes.crouch },
        roll: { ...tuning.modes.roll },
        pen: { ...tuning.modes.pen },
      },
      focus: { ...tuning.focus },
    };
    const follow = this.tuning.modes.follow;
    this.#distance = follow.distance;
    this.#height = follow.height;
    this.#lookHeight = follow.lookHeight;
    this.#framingFov = follow.fov;
    this.#shoulder = follow.shoulder;
    this.#riseAllowance = follow.rise;
    this.#minDistance = follow.minDistance;
    this.#settle = follow.settle;
    this.#armDistance = Math.hypot(follow.distance, follow.shoulder);
    this.#aspect = aspect;
    // Born adapted, so a phone does not watch the lens widen for the first
    // third of a second of the game.
    this.#rigFov = adaptFovForAspect(follow.fov, aspect);
    this.#effectiveFov = this.#rigFov;
    this.#effectiveMinDistance = follow.minDistance;

    // Near is intentionally only 3 cm: at Rosalie's scale a human-camera near plane
    // would crop the surfaces that make proximity and collision readable.
    this.camera = new THREE.PerspectiveCamera(this.#rigFov, aspect, 0.03, 100);
    this.#pivot.set(0, follow.height, 0);
    this.#rigPosition.set(follow.shoulder, follow.height, -follow.distance);
    this.#lookAt.set(0, follow.lookHeight, 0);
    this.#setLookQuaternion(this.#rigPosition, this.#rigQuaternion);
    this.camera.position.copy(this.#rigPosition);
    this.camera.quaternion.copy(this.#rigQuaternion);
  }

  /** Where she is (ground-contact origin) and which way she faces, each render step. */
  setTarget(x: number, y: number, z: number, facing: number): void {
    this.#target.set(x, y, z);
    this.#facing = facing;
  }

  setMode(mode: CameraMode): void {
    if (this.#disposed || mode === this.#mode) return;

    this.#restartModeBlend();
    this.#mode = mode;
  }

  /**
   * Re-read the active mode's live tuning without changing modes.
   *
   * A settled mode never enters `#updateModeBlend`, so mutating its tuning used
   * to leave the already-live rig on its old framing forever. That made a
   * parameter sweep photograph one candidate repeatedly while its labels said
   * otherwise; restart from the in-between values so live debug tuning follows
   * the same no-snap path as a real mode change.
   */
  refreshMode(): void {
    if (this.#disposed) return;
    this.#restartModeBlend();
  }

  /**
   * What she is watching this render step, in world space, or null when she is
   * not watching anything (D-066).
   *
   * The integrator calls this every frame with the live position of the
   * interaction system's observe target while `observe` is held. It is a point,
   * not an id and not an object: the rig frames geometry and has no business
   * knowing that the geometry is a worker.
   *
   * A non-finite point is treated as no focus rather than thrown on. This is
   * driven from a moving simulation entity every single frame, and the failure
   * it guards against — one NaN reaching the look matrix — is a black screen
   * that outlives the bad frame by however long the quaternion takes to recover,
   * which is for ever.
   */
  setFocus(point: { x: number; y: number; z: number } | null): void {
    if (this.#disposed) return;
    if (
      point === null
      || !Number.isFinite(point.x) || !Number.isFinite(point.y) || !Number.isFinite(point.z)
    ) {
      this.#focusActive = false;
      return;
    }
    this.#focusPoint.set(point.x, point.y, point.z);
    this.#focusActive = true;
  }

  /**
   * How far the rig has committed to the focus, 0..1.
   *
   * Exposed for the harness for the reason `rise` is: "holding `Q` reframes
   * toward the worker" is otherwise a claim about a picture, and a picture is
   * exactly the evidence this project has already once accepted without looking
   * (D-035).
   */
  get focusWeight(): number {
    return this.#focusWeight;
  }

  /** Accumulated mouse delta since the last call, in pixels. */
  addLook(dx: number, dy: number): void {
    if (this.#disposed || (dx === 0 && dy === 0)) return;
    this.#yaw -= dx * this.tuning.mouseSensitivity;
    this.#pitch = THREE.MathUtils.clamp(
      this.#pitch - dy * this.tuning.mouseSensitivity,
      this.tuning.pitchMin,
      this.tuning.pitchMax,
    );
    this.#mouseMoved = true;
  }

  /**
   * Whether a look finger is resting on the screen, moving or not.
   *
   * A touch drag delivers deltas only while the finger moves, so a thumb that
   * had placed the view and was holding it there read as idle, and the
   * recentre swung the camera back under it after `autoYawDelay`. A mouse has
   * no equivalent state; this is only ever true on touch.
   */
  setLookHeld(held: boolean): void {
    this.#lookHeld = held;
  }

  /** dt is REAL frame time, not the sim step. */
  update(dt: number, sweep: SweepFn | null): void {
    if (this.#disposed || this.#overridden) return;

    const frameDt = Math.max(0, dt);
    this.#updateModeBlend(frameDt);
    // Before the yaw, because the yaw is one of the things focus drives and a
    // frame of lag between the push and the swing reads as the camera changing
    // its mind twice.
    this.#updateFocus(frameDt);
    this.#updateYaw(frameDt);

    const positionAlpha = 1 - Math.exp(-this.tuning.positionDamping * frameDt);
    const rotationAlpha = 1 - Math.exp(-this.tuning.rotationDamping * frameDt);
    const fovAlpha = 1 - Math.exp(-this.tuning.fovDamping * frameDt);
    this.#buildDesiredPosition();

    const desiredDistance = this.#sweepDirection
      .subVectors(this.#desiredPosition, this.#pivot)
      .length();
    if (desiredDistance > 0) this.#sweepDirection.multiplyScalar(1 / desiredDistance);

    let safeDistance = desiredDistance;
    if (sweep && desiredDistance > 0) {
      const hitDistance = sweep(
        this.#pivot,
        this.#sweepDirection,
        desiredDistance,
        this.tuning.collisionRadius,
      );
      if (hitDistance !== null) {
        // The sweep reports where its centre reaches geometry; subtract its radius
        // before placing the camera, so it cannot finish inside the wall.
        //
        // Clamped below by `minDistance`: in a tight spot we would rather clip a
        // wall than collapse the arm onto the pivot, which points the lens at
        // the top of her head from inside her body.
        safeDistance = Math.min(
          desiredDistance,
          Math.max(this.#effectiveMinDistance, hitDistance - this.tuning.collisionRadius),
        );
      }
    }

    // How much of the arm the room took away, 0..1, fed back into pitch next
    // frame. `minDistance` is subtracted out of both ends so that a mode whose
    // arm is already near the floor does not read as permanently blocked.
    const blockage = desiredDistance > this.#effectiveMinDistance
      ? Math.max(0, Math.min(1,
        (desiredDistance - safeDistance) / (desiredDistance - this.#effectiveMinDistance)))
      : 0;
    // The climb is switched OFF by focus, not merely damped by it.
    //
    // `rise` exists because a blocked arm has to find somewhere to stand and the
    // only free direction is up. A focused arm has a floor instead, and the two
    // together are worse than either: the floor holds the arm out, the sweep still
    // reports the crate, and the climb answers a question that is no longer being
    // asked by tipping the lens onto her back — which is the exact plan view this
    // whole decision exists to get rid of. Composition is `#buildLookAt`'s job now.
    const riseTarget = blockage * this.#riseAllowance * (1 - this.#focusWeight);
    this.#rise += (riseTarget - this.#rise) * (1 - Math.exp(-this.tuning.riseDamping * frameDt));
    // Never above what the current mode allows, so a mode with `rise: 0` has
    // none the moment its blend completes rather than a few frames later.
    // Leaving it to decay on its own let residual pitch bleed out of `pen` and
    // into `follow` for about a fifth of a second — small, and `follow` is the
    // framing a human signed off at G2, so it does not get to move at all.
    // Found on independent review.
    this.#rise = Math.min(this.#rise, this.#riseAllowance * (1 - this.#focusWeight));

    const pulledIn = safeDistance < this.#armDistance;
    if (pulledIn) {
      // Collision contraction is immediate. Smoothing this path is how a camera
      // visibly enters a wall before the arm catches up.
      this.#armDistance = safeDistance;
    } else {
      // Expansion, by contrast, is deliberately capped to stop the classic pop
      // when a wall leaves the sweep volume.
      this.#armDistance = Math.min(
        safeDistance,
        this.#armDistance + this.tuning.collisionRecoverSpeed * frameDt,
      );
    }

    this.#safePosition.copy(this.#pivot).addScaledVector(this.#sweepDirection, this.#armDistance);
    this.#settleLens(desiredDistance, sweep);
    if (pulledIn) {
      this.#rigPosition.copy(this.#safePosition);
    } else {
      this.#rigPosition.lerp(this.#safePosition, positionAlpha);
    }

    // The FOV first, because the focused aim is a composition rule expressed in
    // frame heights and it has to be told what the frame currently is. A frame
    // of lag here reads as the lens settling twice on every push.
    this.#rigFov += (this.#effectiveFov - this.#rigFov) * fovAlpha;
    this.#buildLookAt();
    this.#setLookQuaternion(this.#rigPosition, this.#targetQuaternion);
    this.#rigQuaternion.slerp(this.#targetQuaternion, rotationAlpha);
    this.#limitNarrowFrameLag();

    this.#applyCamera(frameDt);
  }

  /**
   * Cap the rotation lag on a narrow frame (`NARROW_FRAME_MAX_LAG_NDC`).
   *
   * Only below `AUTHORED_ASPECT`, so the follow framing a human signed off at
   * G2 on a 16:9 screen is untouched. The bound is taken from the frame's
   * narrower half-extent — the horizontal one in portrait, the vertical one in
   * a very wide window — so it holds on both axes.
   */
  #limitNarrowFrameLag(): void {
    if (this.#aspect >= AUTHORED_ASPECT) return;
    const tanHalf = Math.tan(THREE.MathUtils.degToRad(this.#rigFov) / 2);
    const maxLag = Math.atan(NARROW_FRAME_MAX_LAG_NDC * tanHalf * Math.min(1, this.#aspect));
    const lag = this.#rigQuaternion.angleTo(this.#targetQuaternion);
    if (lag > maxLag) this.#rigQuaternion.rotateTowards(this.#targetQuaternion, lag - maxLag);
  }

  /**
   * True while an authored framing owns the camera.
   *
   * The contextual prompt reads this: a shot the player is not driving must not
   * carry a key prompt over it, and that is as true of a cinematic in the game
   * as it is of a turnaround in `docs/verification/`.
   */
  get overridden(): boolean {
    return this.#overridden;
  }

  /**
   * Extra pitch the room is currently forcing on the arm, radians.
   *
   * Exposed for the harness only: "the camera climbed out of the pen" is
   * otherwise a claim about a picture, and this project has already shipped one
   * beat on the strength of a picture nobody looked at (D-035).
   */
  get rise(): number {
    return this.#rise;
  }

  /**
   * Where her aim point (the target plus the mode's look height) lands in the
   * presented frame, in NDC. Harness only: "she stays in the frame on a phone"
   * is otherwise a claim about a picture (touch plan, stage 3).
   */
  get subjectNdc(): { x: number; y: number } {
    this.camera.updateMatrixWorld();
    const p = new THREE.Vector3(this.#target.x, this.#target.y + this.#lookHeight, this.#target.z)
      .project(this.camera);
    return { x: p.x, y: p.y };
  }

  /** The active framing mode, rather than the mode an outside state machine wants next. */
  get mode(): CameraMode {
    return this.#mode;
  }

  /** The rig yaw used by camera-relative movement, exposed for harness equality checks. */
  get yaw(): number {
    return this.#yaw;
  }

  /**
   * Lens-to-pivot arm length as it is actually presented this frame.
   *
   * `#armDistance` is the collision-safe target, but render damping can leave
   * `camera.position` between it and `#pivot`; exposing that target as the live
   * arm would turn a measurement into an optimistic approximation.
   */
  get armDistance(): number {
    return this.camera.position.distanceTo(this.#pivot);
  }

  /**
   * Test/debug only: choose the yaw that `getMoveBasis()` will expose to a
   * fixed tick. Gameplay still writes yaw through mouse look and render-time
   * idle recentering; this narrow seam exists so a probe can name a world
   * heading without bypassing the camera-relative movement path it is testing.
   */
  setYaw(radians: number): void {
    if (!Number.isFinite(radians)) throw new Error(`setYaw: non-finite yaw ${String(radians)}`);
    this.#yaw = radians;
  }

  /** Camera-relative movement basis for WASD. XZ only, y stripped, normalised. */
  getMoveBasis(out: MoveBasis): void {
    moveBasisFromYaw(this.#yaw, out);
  }

  setAspect(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
    // Re-scale the lens the rig holds NOW, not only its target: `#updateFocus`
    // would re-adapt the target next frame, but it does not run while an
    // authored shot owns the camera, and a cut hand-back (`releaseShot(false)`)
    // lands on `#rigFov` — which would still be the old aspect's lens. The
    // frame changed shape this instant anyway; the lens changes with it.
    const ratio = aspectLensScale(aspect) / aspectLensScale(this.#aspect);
    if (ratio !== 1) {
      const rescale = (fov: number): number =>
        THREE.MathUtils.radToDeg(2 * Math.atan(Math.tan(THREE.MathUtils.degToRad(fov) / 2) * ratio));
      this.#rigFov = rescale(this.#rigFov);
      this.#effectiveFov = rescale(this.#effectiveFov);
    }
    this.#aspect = aspect;
  }

  /** Hard cut to an authored framing; the rig stops updating until released. */
  overrideShot(position: THREE.Vector3, lookAt: THREE.Vector3, fov: number): void {
    if (this.#disposed) return;
    this.#overridden = true;
    this.#shotEaseIn = false;
    this.#releasing = false;
    this.camera.position.copy(position);
    this.#lookAt.copy(lookAt);
    this.#setLookQuaternion(this.camera.position, this.camera.quaternion);
    this.camera.fov = fov;
    this.camera.updateProjectionMatrix();
  }

  /** Ease a contextual room look in while leaving authored director cuts untouched. */
  overrideShotEased(position: THREE.Vector3, lookAt: THREE.Vector3, fov: number, frameDt: number): void {
    if (this.#disposed) return;
    if (!this.#overridden) {
      this.#overridden = true;
      this.#releasing = false;
      this.#shotEaseIn = true;
    }
    const blend = this.#shotEaseIn
      ? Math.min(1, Math.max(0, frameDt) / Math.max(this.tuning.modeBlendTime, 0.001))
      : 1;
    this.camera.position.lerp(position, blend);
    this.#lookAt.lerp(lookAt, blend);
    this.#setLookQuaternion(this.camera.position, this.camera.quaternion);
    this.camera.fov += (fov - this.camera.fov) * blend;
    this.camera.updateProjectionMatrix();
    if (blend >= 1 || this.camera.position.distanceToSquared(position) < 0.000001) this.#shotEaseIn = false;
  }

  releaseShot(eased: boolean): void {
    if (this.#disposed || !this.#overridden) return;
    this.#overridden = false;
    this.#shotEaseIn = false;
    this.#releaseElapsed = 0;
    this.#releasing = eased && this.tuning.modeBlendTime > 0;

    if (this.#releasing) {
      this.#releasePosition.copy(this.camera.position);
      this.#releaseQuaternion.copy(this.camera.quaternion);
      this.#releaseFov = this.camera.fov;
      return;
    }

    // A non-eased release is the explicit cut counterpart to an authored shot.
    this.camera.position.copy(this.#rigPosition);
    this.camera.quaternion.copy(this.#rigQuaternion);
    this.camera.fov = this.#rigFov;
    this.camera.updateProjectionMatrix();
  }

  dispose(): void {
    // This rig owns no GPU resources or event listeners. Marking it inert makes
    // an accidental render callback after teardown harmless without disposing a
    // camera the integrator may still be removing from its scene graph.
    this.#disposed = true;
  }

  #updateModeBlend(dt: number): void {
    if (!this.#modeBlending) return;

    const target = this.tuning.modes[this.#mode];
    if (this.tuning.modeBlendTime <= 0) {
      this.#distance = target.distance;
      this.#height = target.height;
      this.#lookHeight = target.lookHeight;
      this.#framingFov = target.fov;
      this.#shoulder = target.shoulder;
      this.#riseAllowance = target.rise;
      this.#minDistance = target.minDistance;
      this.#settle = target.settle;
      this.#modeBlending = false;
      return;
    }

    this.#modeBlendElapsed = Math.min(
      this.tuning.modeBlendTime,
      this.#modeBlendElapsed + dt,
    );
    const progress = this.#modeBlendElapsed / this.tuning.modeBlendTime;
    // Smoothstep has zero velocity at both ends, so a state change reads as an
    // intentional camera move rather than a mechanical parameter switch.
    const eased = progress * progress * (3 - 2 * progress);
    this.#distance = this.#blendDistance + (target.distance - this.#blendDistance) * eased;
    this.#height = this.#blendHeight + (target.height - this.#blendHeight) * eased;
    this.#lookHeight = this.#blendLookHeight + (target.lookHeight - this.#blendLookHeight) * eased;
    this.#framingFov = this.#blendFov + (target.fov - this.#blendFov) * eased;
    this.#shoulder = this.#blendShoulder + (target.shoulder - this.#blendShoulder) * eased;
    this.#riseAllowance = this.#blendRise + (target.rise - this.#blendRise) * eased;
    this.#minDistance = this.#blendMinDistance
      + (target.minDistance - this.#blendMinDistance) * eased;
    this.#settle = this.#blendSettle + (target.settle - this.#blendSettle) * eased;
    this.#modeBlending = progress < 1;
  }

  /** Start a framing transition from the values the viewer is currently seeing. */
  #restartModeBlend(): void {
    // Start from the current in-between framing so rapidly changing state never snaps.
    this.#blendDistance = this.#distance;
    this.#blendHeight = this.#height;
    this.#blendLookHeight = this.#lookHeight;
    this.#blendFov = this.#framingFov;
    this.#blendShoulder = this.#shoulder;
    this.#blendRise = this.#riseAllowance;
    this.#blendMinDistance = this.#minDistance;
    this.#blendSettle = this.#settle;
    this.#modeBlendElapsed = 0;
    this.#modeBlending = true;
  }

  /**
   * Ease the focus weight, then lay the focused framing over the blended mode.
   *
   * The mode's own values are left alone. Focus is a modifier, so
   * `#restartModeBlend` still starts from the authored framing and a mode change
   * that happens mid-focus blends underneath it rather than fighting it.
   */
  #updateFocus(dt: number): void {
    const focus = this.tuning.focus;
    const speed = this.#focusActive ? focus.engageSpeed : focus.releaseSpeed;
    const target = this.#focusActive ? 1 : 0;
    this.#focusWeight += (target - this.#focusWeight) * (1 - Math.exp(-speed * dt));
    // Snap the tail. An exponential never arrives, and a weight of 1e-9 still
    // takes the yaw off the player for ever — the auto-recentre below is gated
    // on focus being inactive, not on it being small.
    if (!this.#focusActive && this.#focusWeight < 1e-3) this.#focusWeight = 0;

    const weight = this.#focusWeight;
    // Adapted AFTER focus is laid over the mode, and once, here: `#rigFov`
    // eases toward this, and the focused aim's `atan` inversion, the release
    // blend and `camera.fov` all read `#rigFov` — so they agree on one lens.
    // Authored shots (`overrideShot*`) are deliberately not adapted: some
    // crop tightly on purpose, and each is judged on its own (touch plan,
    // stage 4).
    this.#effectiveFov = adaptFovForAspect(
      this.#framingFov + weight * (focus.fov - this.#framingFov),
      this.#aspect,
    );
    // Never BELOW the mode's own floor: `follow` is allowed to pull in closer
    // than a focused pen ever should, and focus must not shove it back out.
    const floor = Math.max(this.#minDistance, focus.armFloor);
    this.#effectiveMinDistance = this.#minDistance + weight * (floor - this.#minDistance);
  }

  #updateYaw(dt: number): void {
    // A resting look finger counts as looking (`setLookHeld`).
    const moved = this.#mouseMoved || this.#lookHeld;
    this.#mouseMoved = false;
    if (moved) this.#mouseIdle = 0;
    else this.#mouseIdle += dt;

    // `#focusActive`, NOT `#focusWeight > 0`, and the difference is two seconds
    // of stolen mouse look.
    //
    // Everything else about focus eases out with the weight, which is right — the
    // FOV and the composition should settle back rather than cut. The YAW must
    // not: the weight takes about two seconds to decay from 1 at 3.5 / s, and
    // for all of it this branch was still steering toward a bearing the player
    // had already let go of, springing back against every mouse delta. The
    // comment two lines down claimed "letting go of `observe` is how you look
    // somewhere else" while the code made letting go do nothing for two seconds.
    // Found on independent review.
    if (this.#focusActive && this.#focusWeight > 0) {
      // Focus outranks both the idle recentre and, deliberately, the player's
      // own look this frame: they can push against it and it springs back,
      // because letting go of `observe` is how you look somewhere else. Scaled
      // by the weight so the swing arrives with the push instead of ahead of it.
      const bearing = Math.atan2(
        this.#focusPoint.x - this.#target.x,
        this.#focusPoint.z - this.#target.z,
      );
      const alpha = 1 - Math.exp(-this.tuning.focus.yawRate * this.#focusWeight * dt);
      this.#yaw += this.#shortestAngle(bearing - this.#yaw) * alpha;
      return;
    }

    if (moved) return;
    if (this.#mouseIdle <= this.tuning.autoYawDelay || this.tuning.autoYawFollow <= 0) return;

    // This is rotational damping too: it follows the shortest angular path and
    // uses the same exponential form as the rest of the render-time smoothing.
    const alpha = 1 - Math.exp(-this.tuning.autoYawFollow * dt);
    this.#yaw += this.#shortestAngle(this.#facing - this.#yaw) * alpha;
  }

  /**
   * Aim: at her, or — while focusing — wherever puts her on `foregroundLine`.
   *
   * The lens sits above and behind her and therefore looks steeply DOWN at her
   * back, which is what keeps the rest of the room out of frame. Tilting up is
   * the whole reframe, and the question is only how far. Tilting toward the
   * subject is unbounded and loses her; tilting a fixed number of degrees is
   * bounded and does not reach. So the tilt is solved for instead: put her at a
   * chosen height in the frame, and everything else follows from where the room
   * actually is.
   *
   * The inversion is exact. A point α above the aim axis lands at
   * `tan α / tan(fov / 2)` in NDC, so wanting her at `foregroundLine` means
   * aiming `atan(|foregroundLine| · tan(fov / 2))` above her — one `atan`, no
   * search, and correct at every FOV the blend passes through on the way in.
   */
  #buildLookAt(): void {
    this.#lookAt.set(
      this.#target.x,
      this.#target.y + this.#lookHeight,
      this.#target.z,
    );
    if (this.#focusWeight <= 0) return;

    const dx = this.#lookAt.x - this.#rigPosition.x;
    const dz = this.#lookAt.z - this.#rigPosition.z;
    const horizontal = Math.hypot(dx, dz);
    // A lens directly above her has no horizontal baseline to tilt along, and
    // the arithmetic below would divide by it. There is no composition to make
    // from a plan view anyway.
    if (horizontal < 1e-3) return;

    const half = THREE.MathUtils.degToRad(this.#rigFov) / 2;
    const toHer = Math.atan2(this.#lookAt.y - this.#rigPosition.y, horizontal);
    // Negated, not `abs`. The shipped line is negative and `abs` gives the same
    // answer for it, but a positive `foregroundLine` — put her ABOVE centre —
    // would then still aim above her and push her off the top instead. Sign-
    // correct costs nothing and stops the tuning knob having a silent half.
    const above = Math.atan(-this.tuning.focus.foregroundLine * Math.tan(half));
    // Clamped short of vertical. `tan` at 90 degrees is an infinite look height
    // and one non-finite value in the look matrix is a black screen that outlasts
    // the frame that caused it, so the guard is cheap at any price.
    const aim = THREE.MathUtils.clamp(toHer + above, -1.4, 1.4);
    const composed = this.#rigPosition.y + horizontal * Math.tan(aim);
    // Only the height moves. The heading is the yaw's job and it is already on
    // the bearing to what she is watching.
    this.#lookAt.y += (composed - this.#lookAt.y) * this.#focusWeight;
  }

  /**
   * Lower a shortened arm's lens toward her look point (`settle`).
   *
   * The pivot sits 0.72 m up and the look point 0.24 m, so the unblocked
   * `follow` arm looks down at her at about 15 degrees. The sweep shortens the
   * arm along the same line, keeping the lens at pivot height, and at the
   * 0.55 m floor that is 41 degrees: against a wall the frame was the floor and
   * her back, whatever she had just turned to face (`docs/backlog.md`, the
   * looks from the bins in beat 6, the shut door in beat 9 and the junction
   * box in beat 12).
   *
   * Driven by `#armDistance`, the smoothed arm, not by this frame's hit, so it
   * contracts with the arm's immediate pull-in and comes back up at the arm's
   * capped recovery rate — never a pop of its own. An unblocked arm has no
   * drop, so the framing G2 signed off is untouched wherever there is room.
   *
   * The lowered lens is swept for too: the drop stops above whatever is under
   * it. A lens that starts inside geometry (the `minDistance` clamp allows
   * that) reports a hit at zero and is not lowered, which is the old camera.
   */
  #settleLens(desiredDistance: number, sweep: SweepFn | null): void {
    if (this.#settle <= 0 || desiredDistance <= 0) return;
    const lost = 1 - Math.min(1, this.#armDistance / desiredDistance);
    const lookY = this.#target.y + this.#lookHeight;
    // The PIVOT's height over the look point, not the lens's. The lens on the
    // shortened arm already carries that arm's share of the player's pitch, so
    // scaling its height scaled the pitch twice and kept the angle only when the
    // view was level (found on review, gated at both pitches). Keeping the
    // angle at any pitch reduces to exactly this.
    let drop = this.#settle * lost * Math.max(0, this.#pivot.y - lookY);
    const floor = this.#target.y + this.tuning.collisionRadius;
    drop = Math.min(drop, Math.max(0, this.#safePosition.y - floor));
    if (drop <= 1e-4) return;
    if (sweep) {
      const hit = sweep(this.#safePosition, DOWN, drop, this.tuning.collisionRadius);
      if (hit !== null) drop = Math.max(0, hit - this.tuning.collisionRadius);
    }
    this.#safePosition.y -= drop;
  }

  #buildDesiredPosition(): void {
    const sinYaw = Math.sin(this.#yaw);
    const cosYaw = Math.cos(this.#yaw);
    // The player's pitch, MINUS whatever the room has forced on us, clamped so
    // the climb can never become a plan view.
    //
    // Minus, and the sign is not obvious enough to leave unexplained: the camera
    // sits at `pivot − distance × forward`, so a POSITIVE pitch puts the lens
    // BELOW the pivot. The first version of this added the rise, drove the arm
    // into the floor, and was saved from being visibly absurd only by the ground
    // clamp at the bottom of this function — which meant the picture did not
    // change at all and the bug read as "the feature does nothing".
    const pitch = Math.max(this.#pitch - this.#rise, -this.tuning.risePitchMax);
    const cosPitch = Math.cos(pitch);
    this.#pivot.set(this.#target.x, this.#target.y + this.#height, this.#target.z);
    this.#forward.set(sinYaw * cosPitch, Math.sin(pitch), cosYaw * cosPitch);
    this.#right.set(cosYaw, 0, -sinYaw);
    this.#desiredPosition
      .copy(this.#pivot)
      .addScaledVector(this.#forward, -this.#distance)
      .addScaledVector(this.#right, this.#shoulder);

    // `setTarget` is a ground-contact origin. Even before a collision sweep is
    // wired in, never let a downward orbit put the lens below that known ground.
    this.#desiredPosition.y = Math.max(
      this.#target.y + this.tuning.collisionRadius,
      this.#desiredPosition.y,
    );
  }

  #applyCamera(dt: number): void {
    if (this.#releasing) {
      this.#releaseElapsed = Math.min(this.tuning.modeBlendTime, this.#releaseElapsed + dt);
      const progress = this.#releaseElapsed / this.tuning.modeBlendTime;
      const eased = progress * progress * (3 - 2 * progress);
      this.camera.position.lerpVectors(this.#releasePosition, this.#rigPosition, eased);
      this.camera.quaternion.slerpQuaternions(
        this.#releaseQuaternion,
        this.#rigQuaternion,
        eased,
      );
      this.camera.fov = this.#releaseFov + (this.#rigFov - this.#releaseFov) * eased;
      this.#releasing = progress < 1;
    } else {
      this.camera.position.copy(this.#rigPosition);
      this.camera.quaternion.copy(this.#rigQuaternion);
      this.camera.fov = this.#rigFov;
    }
    this.camera.updateProjectionMatrix();
  }

  #setLookQuaternion(position: THREE.Vector3, out: THREE.Quaternion): void {
    this.#lookMatrix.lookAt(position, this.#lookAt, this.#up);
    out.setFromRotationMatrix(this.#lookMatrix);
  }

  #shortestAngle(angle: number): number {
    return THREE.MathUtils.euclideanModulo(angle + Math.PI, Math.PI * 2) - Math.PI;
  }
}
